import { useState } from "react";
import {
  Mail, Mic, Store, BookMarked, Sparkles, Calendar,
  ChevronDown, Copy, Check, Heart, FileText, Phone,
} from "lucide-react";
import { toast } from "sonner";

export default function Resources() {
  return (
    <div className="space-y-16" data-testid="resources-page">
      <header>
        <p className="hh-overline mb-3">Resources · Templates · Playbook</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-hh-ink tracking-tight max-w-2xl">
          Quiet tools for the long work of selling a book.
        </h1>
        <p className="mt-4 text-hh-ink2 max-w-2xl text-lg">
          Everything here is publisher-tested. Take what helps, leave the rest — and remember every <em>no</em> brings you closer to a yes.
        </p>
      </header>

      {/* Inspiration banner with author-direct outreach stats */}
      <section className="rounded-2xl p-8 md:p-10 text-white relative overflow-hidden"
               style={{ background: "linear-gradient(135deg, #1A1530 0%, #2708A1 70%, #0822D2 100%)" }}>
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(216,155,0,0.25), transparent 70%)" }} />
        <div className="relative">
          <p className="hh-overline mb-4 text-white/70">Why your voice beats a publisher email — every time</p>
          <h2 className="font-serif text-2xl md:text-3xl leading-snug max-w-3xl mb-7">
            The single most powerful person at any indie bookstore counter is the local author who walks in carrying their own book.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-7">
            <Stat
              big="4×"
              label="more likely to stock a local author"
              sub="Indie bookstores prioritise local voices and customer-request driven stocking — they want to be known for backing community writers."
            />
            <Stat
              big="1 in 50"
              label="cold publisher emails get a yes"
              sub="Pitches without a personal connection or local angle are filtered out quickly — buyer time is the bottleneck, not shelf space."
            />
            <Stat
              big="~50%"
              label="conversion when an author walks in"
              sub="A signed copy, a five-minute chat and a face attached to the book outperforms every catalogue page and every distributor pitch."
            />
          </div>
          <p className="font-serif text-lg md:text-xl text-[#FFD56B] italic max-w-3xl">
            Your story is the asset. Your face is the lever. Your relationship with the bookseller is the moat.
          </p>
          <p className="mt-3 text-white/70 text-sm max-w-3xl">
            That&rsquo;s why every template here is written in <em>your</em> voice, not ours. Booksellers say yes to the author behind the book — not the publisher behind the author.
          </p>
        </div>
      </section>

      {/* Email Templates */}
      <section data-testid="email-templates-section">
        <p className="hh-overline mb-6">Email templates</p>
        <div className="space-y-4">
          <Template
            icon={Mic}
            title="Podcast pitch — invite a host to feature you"
            tag="Podcast"
            subject="[YOUR NAME] for [PODCAST NAME]: [3-WORD THEME OF YOUR BOOK]"
            body={`Hi [HOST FIRST NAME],

I'm reaching out on behalf of Healing House Publishing to introduce a guest I believe will resonate deeply with [PODCAST NAME]'s audience: [YOUR NAME] — [ONE-LINE DESCRIPTION e.g. "memoirist and women's coach"]. [YOUR NAME]'s book, [BOOK TITLE], is a raw and luminous [GENRE — memoir / collection / essay book] that explores [TWO OR THREE THEMES — e.g. migration, motherhood, the slow work of becoming]. Her work speaks to the tender process of [CORE TRANSFORMATION YOUR BOOK OFFERS], beautifully echoing the conversations you hold around [THEME 1] and [THEME 2].

On your podcast, [YOUR NAME] could explore topics such as:
  · [THEME-DRIVEN TALKING POINT 1]
  · [THEME-DRIVEN TALKING POINT 2]
  · [THEME-DRIVEN TALKING POINT 3 — make one cheekily provocative]

Her journey and her book invite listeners into the deep emotional truths of [THEME], a conversation that would feel perfectly aligned with your audience's appetite for meaningful, soul-led dialogue.

I've attached [YOUR NAME]'s media kit, which includes her bio, key talking points, and press-ready assets for your team. If you feel this is a fit, we'd love to explore scheduling her as a guest.

With warmth,
[YOUR NAME / Vanessa on your behalf]
Healing House Publishing
healinghousepublishing.com`}
          />

          <Template
            icon={Store}
            title="Bookstore stockist — your first outreach to a local store"
            tag="Stockist"
            subject="Local author with a fast-selling debut — would love to chat to [BOOKSTORE NAME]"
            body={`Hi [BOOKSTORE OWNER/MANAGER FIRST NAME],

My name is [YOUR NAME], and I'm a local author based right here in [SUBURB/REGION] — I've been a customer of yours for [HOW LONG / WHICH BOOK YOU LAST BOUGHT FROM THEM IF RELEVANT].

I've just published my [debut/second] book, [BOOK TITLE], with Healing House Publishing — a boutique Australian publisher that pays its authors 95% of net compensation and works directly with stores instead of through distributors. That means you and I can talk directly — no catalogue cycles, no middle layer.

[BOOK TITLE] is a [GENRE — memoir / self-help / spiritual guide] that addresses [THE READER'S PRIMARY PAIN POINT] and offers [THE SPECIFIC LIFE-CHANGING OUTCOME YOUR BOOK OFFERS]. I think it would land well with your readers because [ONE SENTENCE ABOUT LOCAL OR THEMATIC RELEVANCE — e.g. "many of your customers are women in midlife navigating similar reinventions, and this is exactly that story"].

Why I'd love to see it on your shelves:
  · Generous 55% wholesale discount on every unit you stock
  · Full returns available in Australia — zero stock risk
  · Direct ordering with me or my publisher — no third-party friction
  · Author-led promotion: I'll happily run an event, sign stock, or do a Q&A for your customers

To make this easy: I'd like to drop you a complimentary copy with zero obligation. Can I post one, or pop in with a signed copy this week?

Thanks so much for the work you do supporting local writers — it matters more than you know.

Warmly,
[YOUR NAME]
Author of [BOOK TITLE] · Published by Healing House Publishing
[YOUR EMAIL] · [YOUR WEBSITE OR SOCIAL]

P.S. If this isn't the right fit for your shelves, would you consider taking 3–5 copies on consignment? Happy to discuss whatever works for you.`}
          />

          <Template
            icon={Mail}
            title="Bookstore follow-up — no response after 7 days"
            tag="Follow-up"
            subject="Quick follow-up: [BOOK TITLE] for [BOOKSTORE NAME]"
            body={`Hi [NAME],

Just gently following up on my note from last week about [BOOK TITLE]. I know your days are full — appreciate any time you can spare to look.

If now isn't right, I completely understand. If you'd like, I can drop a complimentary copy in this week so you can read it on your own time — no pressure, no obligation.

Warmly,
[YOUR NAME]
Author of [BOOK TITLE]`}
          />

          <Template
            icon={Mail}
            title="Bookstore follow-up — they expressed interest, no decision yet"
            tag="Follow-up"
            subject="Next steps for stocking [BOOK TITLE] at [BOOKSTORE NAME]"
            body={`Hi [NAME],

Lovely chatting with you on [DATE] about [BOOK TITLE]. Wanted to check in and see whether anything from my end would help your decision — sample chapter, advance reading copy, blurb cards, a quick Q&A you could run with your team?

Happy to align with whatever ordering rhythm works for you. Would a five-minute call early next week be useful?

Looking forward to (hopefully!) sharing shelf space with you soon.

Best,
[YOUR NAME]
Author of [BOOK TITLE]`}
          />
        </div>
      </section>

      {/* Phone / in-person scripts */}
      <section data-testid="scripts-section">
        <p className="hh-overline mb-6">In-person & phone scripts</p>
        <div className="space-y-4">
          <Template
            icon={Phone}
            title="Walking into a bookstore — opening script"
            tag="In-person"
            body={`Hi [BOOKSTORE NAME / CONTACT], thanks so much for your time — really appreciate it.

I'm [YOUR NAME] — a local author from [SUBURB/REGION], and I've actually been a customer here for a while (I picked up [LAST BOOK YOU BOUGHT FROM THEM] from you recently — loved it).

I've just published my [debut / second] book, [BOOK TITLE], with Healing House Publishing — a boutique Australian house that works directly with stores instead of through distributors. I'd love to ask you a couple of quick questions about your store to see if my book might be a fit, and if it sounds aligned I'll share more. Sound good?

[DISCOVERY QUESTIONS — ASK ONE OR TWO]
  · What kind of titles are doing well for you right now?
  · What kind of reader are you most looking to serve — community, local connection, meaningful stories?
  · Do you stock directly from authors much, or mostly through catalogues and distributors?

[POSITION THE DIFFERENCE — USE THIS PART TO DISRUPT]
The reason I ask is that because my publisher works direct, there's no catalogue cycle and no third party — you can chat to me or them any time, returns are simple, and I can do events, signings, or a Q&A with your customers whenever it suits you. I want to make it easy for you to back me.

[INVITE — DON'T PUSH]
Would you be open to taking a quick look at my book? I have a complimentary copy with me right now — completely no obligation. Read it on your own time, and if it feels like a fit for your shelves we can talk about a small starting order or consignment. Whatever works for you.

[OPTIONAL CLOSE]
And if it's not right for [BOOKSTORE NAME] this season, I'd still love to drop in occasionally and keep you posted on what I'm doing locally — events, signings, that kind of thing. Sound okay?`}
          />
        </div>
      </section>

      {/* Consignment Guide */}
      <section data-testid="consignment-section">
        <p className="hh-overline mb-6">Consignment guide</p>
        <div className="hh-card p-8 md:p-10 space-y-6">
          <p className="text-hh-ink2 leading-relaxed">
            Not every bookstore will buy outright — especially with a debut title. If they offer consignment, it&rsquo;s still a real win: visibility, credibility and local sales.
          </p>
          <ul className="space-y-3 text-hh-ink">
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Drop off 3–5 copies upfront</strong> — depending on shelf space and expected interest.</span></li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Agree the split in writing</strong> — common is 60% store / 40% author. Confirm via email if they don&rsquo;t offer a contract.</span></li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Label your books</strong> with a sticker inside the cover — so readers can reach you.</span></li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Check in every 1–3 months</strong> — top up stock, ask about sales, collect unsold copies.</span></li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Promote the store back</strong> when you promote your book (&ldquo;Available now at [STORE NAME]!&rdquo;).</span></li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-hh-accent shrink-0" /><span><strong>Offer a signing</strong> — even a quick &ldquo;drive-by&rdquo; signing of their stock builds the relationship.</span></li>
          </ul>
          <div className="p-4 rounded-lg bg-hh-muted/60 border border-hh-border text-sm text-hh-ink2">
            <strong className="text-hh-ink">Note:</strong> If you supply consignment copies you own personally, reverse royalties don&rsquo;t apply — you keep what the store pays you.
          </div>
        </div>
      </section>

      {/* Group call */}
      <section data-testid="group-call-section">
        <div className="hh-card p-8 md:p-12 flex flex-col md:flex-row gap-8 items-start md:items-center">
          <span className="h-14 w-14 rounded-full bg-hh-accent/20 grid place-items-center shrink-0">
            <Calendar size={20} strokeWidth={1.5} className="text-hh-primary" />
          </span>
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-hh-ink">Quarterly group call</h3>
            <p className="mt-2 text-hh-ink2 max-w-xl">
              Sixty minutes with Vanessa and your fellow Healing House authors. Free, every quarter — bring one question, leave with a plan.
            </p>
          </div>
          <a href="mailto:support@thebookdoula.com.au?subject=Quarterly%20Group%20Call" className="hh-btn-primary">Book your seat</a>
        </div>
      </section>

      {/* Author Circle */}
      <section data-testid="author-circle-section" className="text-center">
        <p className="hh-overline mb-3 flex items-center justify-center gap-1"><Heart size={12} strokeWidth={1.8} /> Coming soon</p>
        <h3 className="font-serif text-2xl text-hh-ink">The Book Doula Author Circle</h3>
        <p className="mt-2 text-sm text-hh-ink2 max-w-md mx-auto">
          A small, paid circle for authors who want monthly accountability and editorial support. Quietly launching later this year.
        </p>
      </section>
    </div>
  );
}

function Stat({ big, label, sub }) {
  return (
    <div className="rounded-xl bg-white/[0.06] backdrop-blur-sm border border-white/10 p-5">
      <div className="font-serif text-4xl text-[#FFD56B] mb-1">{big}</div>
      <div className="text-sm font-medium text-white mb-2">{label}</div>
      <p className="text-xs text-white/65 leading-relaxed">{sub}</p>
    </div>
  );
}

function Template({ icon: Icon, title, tag, subject, body }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    const txt = subject ? `Subject: ${subject}\n\n${body}` : body;
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      toast.success("Copied — paste into your email and personalise the [BRACKETS].");
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      toast.error("Couldn't copy — please select the text manually.");
    }
  };

  const slug = title.replace(/\s/g, "-").toLowerCase();
  return (
    <article
      className={`hh-card hh-card-hover overflow-hidden transition-all ${open ? "ring-1 ring-hh-primary/30" : ""}`}
      data-testid={`template-${slug}`}
    >
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left p-6 md:p-7 flex gap-5 items-start">
        <span className="shrink-0 h-11 w-11 rounded-full bg-hh-muted grid place-items-center">
          <Icon size={18} strokeWidth={1.5} className="text-hh-primary" />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="font-serif text-lg text-hh-ink">{title}</h3>
            {tag && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-hh-muted text-hh-ink2">{tag}</span>}
          </div>
          {subject && <p className="text-sm text-hh-ink2 font-mono truncate">Subject: {subject}</p>}
        </div>
        <ChevronDown size={18} className={`text-hh-ink2 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-hh-border bg-hh-muted/30">
          <div className="p-6 md:p-8">
            {subject && (
              <div className="mb-5">
                <div className="hh-overline mb-1.5">Subject line</div>
                <p className="font-mono text-sm text-hh-ink bg-white border border-hh-border rounded-lg px-4 py-2.5">{subject}</p>
              </div>
            )}
            <div className="hh-overline mb-1.5">Body</div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-hh-ink leading-relaxed bg-white border border-hh-border rounded-lg p-5">{body}</pre>
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-hh-ink2">Replace every <code className="bg-hh-muted px-1 py-0.5 rounded text-[11px]">[BRACKETED FIELD]</code> with your details before sending.</p>
              <button
                onClick={copyAll}
                className="hh-btn-ghost py-2 text-xs"
                data-testid={`copy-${slug}`}
              >
                {copied ? <><Check size={14} className="mr-1.5 text-hh-sage" /> Copied</> : <><Copy size={14} className="mr-1.5" /> Copy template</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
